import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Select,
  Input,
  Collapse,
  message,
  Tooltip,
  Badge,
  Modal,
  Row,
  Col,
  Empty,
  Popconfirm,
  Divider,
} from "antd";
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  SendOutlined,
  EditOutlined,
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SwapOutlined,
  UploadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import {
  fetchChecklists,
  fetchSingleChecklist,
  createChecklist,
  updateChecklistItems,
  runComparison,
  comparePriorPolicy,
  attachPriorPolicy,
  updateChecklistStatus,
  exportChecklist,
  deleteChecklist,
  setActiveChecklist,
  clearExportData,
} from "../../../../Slices/policyCheckSlice";
import * as XLSX from "xlsx";

const { Text, Title } = Typography;
const { TextArea } = Input;

const MATCH_STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "default" },
  { value: "match", label: "Match", color: "green" },
  { value: "not_match", label: "Not Match", color: "red" },
  { value: "not_found", label: "Not Found", color: "orange" },
  { value: "updated_ams", label: "Updated AMS", color: "blue" },
  { value: "n_a", label: "N/A", color: "default" },
];

const POLICY_TYPE_OPTIONS = [
  { value: "AUTO", label: "Auto" },
  { value: "GL", label: "General Liability" },
  { value: "BOP", label: "BOP" },
  { value: "CPKG", label: "Commercial Package" },
  { value: "WC", label: "Workers Comp" },
  { value: "UMB", label: "Umbrella" },
  { value: "PROP", label: "Property" },
  { value: "CRIM", label: "Crime" },
  { value: "OTHER", label: "Other" },
];

const StatusTag = ({ status }) => {
  const config = MATCH_STATUS_OPTIONS.find((o) => o.value === status) || MATCH_STATUS_OPTIONS[0];
  const icons = {
    match: <CheckCircleOutlined />,
    not_match: <CloseCircleOutlined />,
    not_found: <ExclamationCircleOutlined />,
    pending: <MinusCircleOutlined />,
    updated_ams: <CheckCircleOutlined />,
    n_a: <MinusCircleOutlined />,
  };
  return (
    <Tag icon={icons[status]} color={config.color}>
      {config.label}
    </Tag>
  );
};

const PolicyChecklistView = ({ requestId, RequestInfo }) => {
  const dispatch = useDispatch();
  const {
    checklists,
    activeChecklist,
    documents,
    compareStatus,
    priorCompareStatus,
    priorComparisonSummary,
    priorAISummary,
    exportData,
  } = useSelector((state) => state.policyCheck);

  const [editedItems, setEditedItems] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedPolicyType, setSelectedPolicyType] = useState("CPKG");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [isAMReview, setIsAMReview] = useState(false);
  const [showPriorColumns, setShowPriorColumns] = useState(false);
  const [priorDocModalOpen, setPriorDocModalOpen] = useState(false);
  const [selectedPriorDocId, setSelectedPriorDocId] = useState(null);

  // Load active checklist if there are checklists but none selected
  useEffect(() => {
    if (checklists.length > 0 && !activeChecklist) {
      dispatch(setActiveChecklist(checklists[0]));
      dispatch(fetchSingleChecklist(checklists[0]._id));
    }
  }, [checklists, activeChecklist, dispatch]);

  // Export to Excel when export data arrives
  // Auto-show prior columns if prior data exists
  useEffect(() => {
    if (activeChecklist?.priorPolicyDocumentId || activeChecklist?.items?.some(i => i.priorPolicyValue)) {
      setShowPriorColumns(true);
    }
  }, [activeChecklist?._id]);

  useEffect(() => {
    if (exportData) {
      generateExcel(exportData);
      dispatch(clearExportData());
    }
  }, [exportData, dispatch]);

  const handleCreateChecklist = async () => {
    const completedDoc = documents.find((d) => d.parsingStatus === "completed" && d.policyType === selectedPolicyType);
    try {
      await dispatch(
        createChecklist({
          requestId,
          policyType: selectedPolicyType,
          documentId: completedDoc?._id,
          insuredName: RequestInfo?.insuredName,
        })
      ).unwrap();
      message.success("Checklist created!");
      setCreateModalOpen(false);
    } catch (err) {
      message.error(err || "Failed to create checklist");
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setEditedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        _id: itemId,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    const updates = Object.values(editedItems);
    if (updates.length === 0) {
      message.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      await dispatch(
        updateChecklistItems({
          checklistId: activeChecklist._id,
          items: updates,
        })
      ).unwrap();
      setEditedItems({});
      message.success(`${updates.length} items saved`);
    } catch (err) {
      message.error(err || "Failed to save");
    }
    setSaving(false);
  };

  const handleRunComparison = async () => {
    // Save pending edits first
    if (Object.keys(editedItems).length > 0) {
      await handleSave();
    }

    try {
      const result = await dispatch(runComparison(activeChecklist._id)).unwrap();
      message.success(
        `Comparison complete! Score: ${result.summary.score}% | ${result.summary.matchCount} matches, ${result.summary.notMatchCount} mismatches, ${result.summary.notFoundCount} not found`
      );
    } catch (err) {
      message.error(err || "Comparison failed");
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await dispatch(
        updateChecklistStatus({
          checklistId: activeChecklist._id,
          status: newStatus,
        })
      ).unwrap();
      message.success(`Status updated to: ${newStatus}`);
    } catch (err) {
      message.error(err || "Failed to update status");
    }
  };

  const handleAttachPrior = async () => {
    if (!selectedPriorDocId) {
      message.warning("Select a prior policy document first");
      return;
    }
    try {
      await dispatch(
        attachPriorPolicy({
          checklistId: activeChecklist._id,
          priorDocumentId: selectedPriorDocId,
        })
      ).unwrap();
      message.success("Prior policy attached — data pre-filled from AI extraction");
      setPriorDocModalOpen(false);
      setShowPriorColumns(true);
    } catch (err) {
      message.error(err || "Failed to attach prior policy");
    }
  };

  const handleComparePrior = async () => {
    if (Object.keys(editedItems).length > 0) {
      await handleSave();
    }
    try {
      const result = await dispatch(comparePriorPolicy(activeChecklist._id)).unwrap();
      setShowPriorColumns(true);
      const s = result.summary;
      message.success(
        `Prior comparison done! Premium ${s.premiumChange?.available ? (s.premiumChange.direction === 'increase' ? `↑ $${Math.abs(s.premiumChange.difference).toLocaleString()} (${s.premiumChange.percentChange}%)` : s.premiumChange.direction === 'decrease' ? `↓ $${Math.abs(s.premiumChange.difference).toLocaleString()} (${s.premiumChange.percentChange}%)` : 'No change') : 'N/A'} | ${s.increased} increased, ${s.decreased} decreased, ${s.added} added, ${s.removed} removed`
      );
    } catch (err) {
      message.error(err || "Prior comparison failed");
    }
  };

  const handleExport = () => {
    dispatch(exportChecklist(activeChecklist._id));
  };

  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws);

        // Map imported rows back to checklist items
        const updates = {};
        for (const row of data) {
          const item = activeChecklist.items.find(
            (i) => i.fieldName === row["Field Name"] && i.section === row["Section"]
          );
          if (!item) continue;

          const changes = {};
          if (row["AMS Value"] !== undefined) changes.partnerXEValue = String(row["AMS Value"]);
          if (row["Match Status"]) {
            const statusMap = {
              Match: "match",
              "Not Match": "not_match",
              "Not Found": "not_found",
              "Updated AMS": "updated_ams",
              Pending: "pending",
              "N/A": "n_a",
            };
            changes.matchStatus = statusMap[row["Match Status"]] || row["Match Status"];
          }
          if (row["Comment"]) changes.comment = row["Comment"];
          if (row["AM Match Status"]) changes.amMatchStatus = row["AM Match Status"].toLowerCase().replace(/ /g, "_");
          if (row["AM Comment"]) changes.amComment = row["AM Comment"];

          if (Object.keys(changes).length > 0) {
            updates[item._id] = { _id: item._id, ...changes };
          }
        }

        if (Object.keys(updates).length > 0) {
          dispatch(
            updateChecklistItems({
              checklistId: activeChecklist._id,
              items: Object.values(updates),
            })
          );
          message.success(`Imported ${Object.keys(updates).length} items from Excel`);
        } else {
          message.warning("No matching items found in the imported file");
        }
      } catch (err) {
        message.error("Failed to parse Excel file");
        console.error(err);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  // Get unique sections for filter
  const sections = activeChecklist
    ? [...new Set(activeChecklist.items.map((i) => i.section))]
    : [];

  // Filter items
  const filteredItems = activeChecklist
    ? activeChecklist.items.filter((item) => {
        if (sectionFilter !== "all" && item.section !== sectionFilter) return false;
        if (statusFilter !== "all" && item.matchStatus !== statusFilter) return false;
        return true;
      })
    : [];

  // Group items by section for display
  const groupedItems = {};
  for (const item of filteredItems) {
    if (!groupedItems[item.section]) groupedItems[item.section] = [];
    groupedItems[item.section].push(item);
  }

  const columns = [
    {
      title: "Field",
      dataIndex: "fieldName",
      key: "fieldName",
      width: 250,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Policy Document Value",
      dataIndex: "policyValue",
      key: "policyValue",
      width: 200,
      render: (val, record) => {
        const edited = editedItems[record._id]?.policyValue;
        return (
          <Input
            size="small"
            value={edited !== undefined ? edited : val}
            onChange={(e) => handleItemChange(record._id, "policyValue", e.target.value)}
            placeholder="Enter from policy doc"
          />
        );
      },
    },
    {
      title: "Page #",
      dataIndex: "pageNumber",
      key: "pageNumber",
      width: 70,
      render: (val, record) => {
        const edited = editedItems[record._id]?.pageNumber;
        return (
          <Input
            size="small"
            value={edited !== undefined ? edited : val}
            onChange={(e) => handleItemChange(record._id, "pageNumber", e.target.value)}
            placeholder="#"
            style={{ width: 50 }}
          />
        );
      },
    },
    {
      title: "AMS Value",
      dataIndex: "partnerXEValue",
      key: "partnerXEValue",
      width: 200,
      render: (val, record) => {
        const edited = editedItems[record._id]?.partnerXEValue;
        return (
          <Input
            size="small"
            value={edited !== undefined ? edited : val}
            onChange={(e) => handleItemChange(record._id, "partnerXEValue", e.target.value)}
            placeholder="Enter from AMS"
            style={{
              borderColor: record.matchStatus === "not_match" ? "#ff4d4f" : undefined,
            }}
          />
        );
      },
    },
    {
      title: "Match?",
      dataIndex: "matchStatus",
      key: "matchStatus",
      width: 140,
      render: (val, record) => {
        const edited = editedItems[record._id]?.matchStatus;
        return (
          <Select
            size="small"
            value={edited || val}
            style={{ width: 130 }}
            onChange={(v) => handleItemChange(record._id, "matchStatus", v)}
          >
            {MATCH_STATUS_OPTIONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                <StatusTag status={opt.value} />
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      width: 200,
      render: (val, record) => {
        const edited = editedItems[record._id]?.comment;
        return (
          <TextArea
            size="small"
            value={edited !== undefined ? edited : val}
            onChange={(e) => handleItemChange(record._id, "comment", e.target.value)}
            placeholder={record.matchStatus === "not_match" ? "Required for Not Match" : "Optional"}
            autoSize={{ minRows: 1, maxRows: 3 }}
            style={{
              borderColor: record.matchStatus === "not_match" && !val && !edited ? "#ff4d4f" : undefined,
            }}
          />
        );
      },
    },
  ];

  // AM Review columns (appended when in AM review mode)
  // Prior Policy Comparison columns
  if (showPriorColumns) {
    // Insert after "Match?" column (index 4) and before AM columns
    columns.push(
      {
        title: "Prior/Expiring Value",
        dataIndex: "priorPolicyValue",
        key: "priorPolicyValue",
        width: 200,
        render: (val, record) => {
          const edited = editedItems[record._id]?.priorPolicyValue;
          return (
            <Input
              size="small"
              value={edited !== undefined ? edited : val}
              onChange={(e) => handleItemChange(record._id, "priorPolicyValue", e.target.value)}
              placeholder="Prior policy value"
              style={{
                backgroundColor: "#f6ffed",
                borderColor: record.priorMatchStatus === "not_match" ? "#ff4d4f" : "#b7eb8f",
              }}
            />
          );
        },
      },
      {
        title: "vs Prior?",
        dataIndex: "priorMatchStatus",
        key: "priorMatchStatus",
        width: 130,
        render: (val, record) => {
          const edited = editedItems[record._id]?.priorMatchStatus;
          const current = edited || val;
          return (
            <Select
              size="small"
              value={current || ""}
              style={{ width: 120 }}
              onChange={(v) => handleItemChange(record._id, "priorMatchStatus", v)}
              allowClear
            >
              {MATCH_STATUS_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  <StatusTag status={opt.value} />
                </Select.Option>
              ))}
            </Select>
          );
        },
      },
      {
        title: "Change",
        dataIndex: "changeType",
        key: "changeType",
        width: 110,
        render: (val, record) => {
          if (!val) return null;
          const config = {
            no_change: { color: "green", icon: <CheckCircleOutlined />, label: "No Change" },
            increased: { color: "red", icon: <ArrowUpOutlined />, label: "Increased" },
            decreased: { color: "blue", icon: <ArrowDownOutlined />, label: "Decreased" },
            added: { color: "purple", icon: <PlusOutlined />, label: "Added" },
            removed: { color: "volcano", icon: <DeleteOutlined />, label: "Removed" },
            modified: { color: "orange", icon: <EditOutlined />, label: "Modified" },
          };
          const c = config[val] || { color: "default", label: val };
          return (
            <Tooltip title={record.changeSummary}>
              <Tag icon={c.icon} color={c.color}>{c.label}</Tag>
            </Tooltip>
          );
        },
      }
    );
  }

  if (isAMReview) {
    columns.push(
      {
        title: "AM Match?",
        dataIndex: "amMatchStatus",
        key: "amMatchStatus",
        width: 130,
        render: (val, record) => {
          const edited = editedItems[record._id]?.amMatchStatus;
          return (
            <Select
              size="small"
              value={edited || val || ""}
              style={{ width: 120 }}
              onChange={(v) => handleItemChange(record._id, "amMatchStatus", v)}
              allowClear
            >
              {MATCH_STATUS_OPTIONS.filter((o) => o.value !== "pending" && o.value !== "n_a").map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  <StatusTag status={opt.value} />
                </Select.Option>
              ))}
            </Select>
          );
        },
      },
      {
        title: "AM Comment",
        dataIndex: "amComment",
        key: "amComment",
        width: 180,
        render: (val, record) => {
          const edited = editedItems[record._id]?.amComment;
          return (
            <TextArea
              size="small"
              value={edited !== undefined ? edited : val}
              onChange={(e) => handleItemChange(record._id, "amComment", e.target.value)}
              autoSize={{ minRows: 1, maxRows: 3 }}
              placeholder="AM comments"
            />
          );
        },
      }
    );
  }

  if (!activeChecklist && checklists.length === 0) {
    return (
      <Card>
        <Empty
          description={
            documents.length === 0
              ? "Upload a policy document first, then create a checklist"
              : "No checklist created yet"
          }
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
            disabled={documents.length === 0 && !RequestInfo}
          >
            Create New Checklist
          </Button>
        </Empty>

        <Modal
          title="Create Policy Checklist"
          open={createModalOpen}
          onOk={handleCreateChecklist}
          onCancel={() => setCreateModalOpen(false)}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text>Select the policy type for this checklist:</Text>
            <Select
              value={selectedPolicyType}
              onChange={setSelectedPolicyType}
              style={{ width: "100%" }}
              options={POLICY_TYPE_OPTIONS}
            />
            {documents.filter((d) => d.parsingStatus === "completed").length > 0 && (
              <Text type="secondary">
                AI-extracted data from uploaded documents will be pre-filled.
              </Text>
            )}
          </Space>
        </Modal>
      </Card>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              {/* Checklist selector if multiple */}
              {checklists.length > 1 && (
                <Select
                  size="small"
                  value={activeChecklist?._id}
                  style={{ width: 200 }}
                  onChange={(id) => {
                    const cl = checklists.find((c) => c._id === id);
                    dispatch(setActiveChecklist(cl));
                    dispatch(fetchSingleChecklist(id));
                  }}
                >
                  {checklists.map((cl) => (
                    <Select.Option key={cl._id} value={cl._id}>
                      {cl.policyType} — {cl.status}
                    </Select.Option>
                  ))}
                </Select>
              )}

              {/* Section filter */}
              <Select
                size="small"
                value={sectionFilter}
                style={{ width: 200 }}
                onChange={setSectionFilter}
              >
                <Select.Option value="all">All Sections</Select.Option>
                {sections.map((s) => (
                  <Select.Option key={s} value={s}>
                    {s}
                  </Select.Option>
                ))}
              </Select>

              {/* Status filter */}
              <Select
                size="small"
                value={statusFilter}
                style={{ width: 150 }}
                onChange={setStatusFilter}
              >
                <Select.Option value="all">All Statuses</Select.Option>
                {MATCH_STATUS_OPTIONS.map((o) => (
                  <Select.Option key={o.value} value={o.value}>
                    {o.label}
                  </Select.Option>
                ))}
              </Select>

              <Tag>{filteredItems.length} items</Tag>
            </Space>
          </Col>

          <Col>
            <Space>
              <Button
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={Object.keys(editedItems).length === 0}
              >
                Save ({Object.keys(editedItems).length})
              </Button>

              <Button
                size="small"
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleRunComparison}
                loading={compareStatus === "comparing"}
              >
                Run AI Comparison
              </Button>

              <Divider type="vertical" />

              <Button
                size="small"
                icon={<UploadOutlined />}
                onClick={() => setPriorDocModalOpen(true)}
                type={activeChecklist?.priorPolicyDocumentId ? "default" : "dashed"}
              >
                {activeChecklist?.priorPolicyDocumentId ? "Change Prior Policy" : "Attach Prior Policy"}
              </Button>

              <Button
                size="small"
                icon={<SwapOutlined />}
                type="primary"
                ghost
                onClick={handleComparePrior}
                loading={priorCompareStatus === "comparing"}
                disabled={!activeChecklist?.priorPolicyDocumentId && !activeChecklist?.items?.some(i => i.priorPolicyValue)}
              >
                Compare New vs Prior
              </Button>

              <Button
                size="small"
                onClick={() => setShowPriorColumns(!showPriorColumns)}
                type={showPriorColumns ? "primary" : "default"}
              >
                {showPriorColumns ? "Hide Prior Columns" : "Show Prior Columns"}
              </Button>

              <Divider type="vertical" />

              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => setIsAMReview(!isAMReview)}
                type={isAMReview ? "primary" : "default"}
              >
                {isAMReview ? "Exit AM Review" : "AM Review Mode"}
              </Button>

              <Button size="small" icon={<DownloadOutlined />} onClick={handleExport}>
                Export Excel
              </Button>

              <label style={{ cursor: "pointer" }}>
                <Button size="small" icon={<UploadOutlined />} onClick={() => document.getElementById("excel-import").click()}>
                  Import Excel
                </Button>
                <input
                  id="excel-import"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleImportExcel}
                />
              </label>

              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalOpen(true)}
              >
                New Checklist
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Status bar */}
        {activeChecklist && (
          <Row style={{ marginTop: 8 }} align="middle">
            <Col>
              <Space>
                <Text type="secondary">Status:</Text>
                <Tag color="blue">{activeChecklist.status}</Tag>

                {activeChecklist.status === "in_progress" && (
                  <Button size="small" onClick={() => handleStatusUpdate("checker_complete")}>
                    Mark Checker Complete
                  </Button>
                )}
                {activeChecklist.status === "checker_complete" && (
                  <Button size="small" onClick={() => handleStatusUpdate("am_review")}>
                    Send to AM Review
                  </Button>
                )}
                {(activeChecklist.status === "am_review" || activeChecklist.status === "checker_complete") && (
                  <Button size="small" type="primary" onClick={() => handleStatusUpdate("completed")}>
                    Mark Complete
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        )}
      </Card>

      {/* Checklist Table — grouped by section */}
      {Object.entries(groupedItems).map(([section, items]) => (
        <Card
          key={section}
          size="small"
          title={
            <Space>
              <Text strong>{section}</Text>
              <Badge
                count={items.filter((i) => i.matchStatus === "not_match").length}
                style={{ backgroundColor: "#ff4d4f" }}
                overflowCount={99}
                showZero={false}
              />
              <Badge
                count={items.filter((i) => i.matchStatus === "not_found").length}
                style={{ backgroundColor: "#faad14" }}
                overflowCount={99}
                showZero={false}
              />
            </Space>
          }
          style={{ marginBottom: 8 }}
        >
          <Table
            columns={columns}
            dataSource={items}
            rowKey="_id"
            pagination={false}
            size="small"
            scroll={{ x: isAMReview ? 1400 : 1100 }}
            rowClassName={(record) => {
              if (record.matchStatus === "not_match") return "policy-row-not-match";
              if (record.matchStatus === "not_found") return "policy-row-not-found";
              return "";
            }}
          />
        </Card>
      ))}

      {/* Create Modal */}
      <Modal
        title="Create New Checklist"
        open={createModalOpen}
        onOk={handleCreateChecklist}
        onCancel={() => setCreateModalOpen(false)}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>Select the policy type:</Text>
          <Select
            value={selectedPolicyType}
            onChange={setSelectedPolicyType}
            style={{ width: "100%" }}
            options={POLICY_TYPE_OPTIONS}
          />
        </Space>
      </Modal>

      {/* Prior Policy Document Selection Modal */}
      <Modal
        title="Attach Prior/Expiring Policy Document"
        open={priorDocModalOpen}
        onOk={handleAttachPrior}
        onCancel={() => setPriorDocModalOpen(false)}
        okText="Attach & Pre-fill"
        okButtonProps={{ disabled: !selectedPriorDocId }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>
            Select the <strong>expiring/prior policy</strong> document to compare against the new policy.
            The AI-extracted data from this document will be used for side-by-side comparison.
          </Text>
          <Select
            value={selectedPriorDocId}
            onChange={setSelectedPriorDocId}
            style={{ width: "100%" }}
            placeholder="Select prior policy document..."
            allowClear
          >
            {documents
              .filter((d) => d._id !== activeChecklist?.policyDocumentId)
              .map((doc) => (
                <Select.Option key={doc._id} value={doc._id}>
                  {doc.fileName} — {doc.policyType || "Unknown type"} ({doc.parsingStatus || doc.status})
                </Select.Option>
              ))}
          </Select>
          {documents.filter((d) => d._id !== activeChecklist?.policyDocumentId).length === 0 && (
            <Text type="secondary">
              No other documents found. Upload the prior/expiring policy PDF first in the Documents tab.
            </Text>
          )}
        </Space>
      </Modal>

      {/* Prior comparison summary banner */}
      {priorComparisonSummary && showPriorColumns && (
        <Card
          size="small"
          style={{ marginTop: 8, background: "#f0f5ff", borderColor: "#91d5ff" }}
        >
          <Row gutter={16} align="middle">
            <Col>
              <Text strong>Renewal Comparison Summary: </Text>
            </Col>
            <Col>
              <Tag color="green">{priorComparisonSummary.noChange || priorComparisonSummary.no_change || 0} No Change</Tag>
              <Tag color="red">{priorComparisonSummary.increased || 0} Increased</Tag>
              <Tag color="blue">{priorComparisonSummary.decreased || 0} Decreased</Tag>
              <Tag color="purple">{priorComparisonSummary.added || 0} Added</Tag>
              <Tag color="volcano">{priorComparisonSummary.removed || 0} Removed</Tag>
              <Tag color="orange">{priorComparisonSummary.modified || 0} Modified</Tag>
            </Col>
            {priorComparisonSummary.premiumChange?.available && (
              <Col>
                <Text strong>Premium: </Text>
                <Tag color={priorComparisonSummary.premiumChange.direction === "increase" ? "red" : priorComparisonSummary.premiumChange.direction === "decrease" ? "green" : "blue"}>
                  {priorComparisonSummary.premiumChange.direction === "increase" ? "↑" : priorComparisonSummary.premiumChange.direction === "decrease" ? "↓" : "="}{" "}
                  ${Math.abs(priorComparisonSummary.premiumChange.difference).toLocaleString()} ({priorComparisonSummary.premiumChange.percentChange})
                </Tag>
              </Col>
            )}
            {(priorComparisonSummary.criticalCount > 0) && (
              <Col>
                <Tag color="red">⚠ {priorComparisonSummary.criticalCount} Critical</Tag>
              </Col>
            )}
          </Row>
          {priorAISummary && (
            <div style={{ marginTop: 8, padding: "8px 12px", background: "#f5f3ff", borderRadius: 6, border: "1px solid #c4b5fd" }}>
              <Text style={{ fontSize: 12, color: "#7c3aed" }}><strong>AI Analysis:</strong> {priorAISummary}</Text>
            </div>
          )}
        </Card>
      )}

      {/* Inline styles for row highlighting */}
      <style>{`
        .policy-row-not-match { background-color: #fff2f0 !important; }
        .policy-row-not-match:hover > td { background-color: #ffedeb !important; }
        .policy-row-not-found { background-color: #fffbe6 !important; }
        .policy-row-not-found:hover > td { background-color: #fff8cc !important; }
      `}</style>
    </div>
  );
};

// Generate Excel file from export data
function generateExcel(data) {
  const wb = XLSX.utils.book_new();

  // Header row
  const headerRows = [
    ["Insured's Name", "", "", data.header.insuredName],
    ["Client", "", "", data.header.client],
    ["Items Compared", "", "", data.header.itemsCompared],
    ["Checked By", "", "", data.header.checkedBy],
    ["Policy Type", "", "", data.header.policyType],
    ["Status", "", "", data.header.status],
    [],
    [
      "Section",
      "Field Name",
      "New Policy Value",
      "Page #",
      "AMS Value",
      "Match vs AMS",
      "Comment",
      "Prior/Expiring Value",
      "Match vs Prior",
      "Change Type",
      "Change Summary",
      "AM Match Status",
      "AM Comment",
      "AM Update",
    ],
  ];

  // Data rows grouped by section
  const dataRows = [];
  for (const [section, items] of Object.entries(data.sections)) {
    for (const item of items) {
      dataRows.push([
        section,
        item.fieldName,
        item.policyValue || "",
        item.pageNumber || "",
        item.partnerXEValue || "",
        formatMatchStatus(item.matchStatus),
        item.comment || "",
        item.priorPolicyValue || "",
        formatMatchStatus(item.priorMatchStatus),
        item.changeType || "",
        item.changeSummary || "",
        formatMatchStatus(item.amMatchStatus),
        item.amComment || "",
        item.amUpdate || "",
      ]);
    }
  }

  const ws = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows]);

  // Set column widths
  ws["!cols"] = [
    { wch: 25 }, // Section
    { wch: 40 }, // Field Name
    { wch: 30 }, // New Policy Value
    { wch: 10 }, // Page
    { wch: 30 }, // AMS Value
    { wch: 15 }, // Match vs AMS
    { wch: 40 }, // Comment
    { wch: 30 }, // Prior/Expiring Value
    { wch: 15 }, // Match vs Prior
    { wch: 15 }, // Change Type
    { wch: 40 }, // Change Summary
    { wch: 15 }, // AM Match
    { wch: 30 }, // AM Comment
    { wch: 20 }, // AM Update
  ];

  XLSX.utils.book_append_sheet(wb, ws, "Coverages");

  // Summary sheet
  const summaryWs = XLSX.utils.aoa_to_sheet([
    ["Summary"],
    ["Total Items", data.summary.totalItems],
    ["Match", data.summary.matchCount],
    ["Not Match", data.summary.notMatchCount],
    ["Not Found", data.summary.notFoundCount],
    ["Pending", data.summary.pendingCount],
  ]);
  XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

  // Dropdown values sheet
  const dropdownWs = XLSX.utils.aoa_to_sheet([
    ["Match"],
    ["Not Match"],
    ["Updated AMS"],
    ["Not Found"],
  ]);
  XLSX.utils.book_append_sheet(wb, dropdownWs, "Dropdowns");

  const fileName = `${data.header.policyType}_Policy_Checklist_${data.header.insuredName || "export"}.xlsx`;
  XLSX.writeFile(wb, fileName);
  message.success(`Exported: ${fileName}`);
}

function formatMatchStatus(status) {
  const map = {
    match: "Match",
    not_match: "Not Match",
    not_found: "Not Found",
    updated_ams: "Updated AMS",
    pending: "Pending",
    n_a: "N/A",
  };
  return map[status] || status || "";
}

export default PolicyChecklistView;
